import {
  useState,
  useRef,
  useCallback
} from 'react'

import {
  motion,
  AnimatePresence
} from 'framer-motion'

import {
  FaUpload,
  FaTrash,
  FaCheck,
  FaExclamationTriangle,
  FaCamera,
  FaEye
} from 'react-icons/fa'

import {
  saveProfileImage,
  deleteProfileImage
} from '../../../api/portfolioApi'

import {
  useAdmin
} from '../../../context/AdminContext'

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const MAX_SIZE_MB = 3

const MAX_SIZE_B =
  MAX_SIZE_MB * 1024 * 1024

const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif'
]

const ALLOWED_EXT =
  '.jpg,.jpeg,.png,.webp,.gif'

// ─────────────────────────────────────────────
// Compress Image
// ─────────────────────────────────────────────
function compressImage(
  file,
  maxDim = 600,
  quality = 0.82
) {

  return new Promise((resolve, reject) => {

    const img = new Image()

    const url =
      URL.createObjectURL(file)

    img.onload = () => {

      URL.revokeObjectURL(url)

      let {
        width,
        height
      } = img

      // Resize
      if (
        width > maxDim
        || height > maxDim
      ) {

        const ratio = Math.min(
          maxDim / width,
          maxDim / height
        )

        width = Math.round(
          width * ratio
        )

        height = Math.round(
          height * ratio
        )
      }

      const canvas =
        document.createElement(
          'canvas'
        )

      canvas.width = width
      canvas.height = height

      const ctx =
        canvas.getContext('2d')

      ctx.drawImage(
        img,
        0,
        0,
        width,
        height
      )

      const dataUrl =
        canvas.toDataURL(
          'image/jpeg',
          quality
        )

      resolve(dataUrl)
    }

    img.onerror = () => {

      URL.revokeObjectURL(url)

      reject(
        new Error(
          'Image load failed'
        )
      )
    }

    img.src = url
  })
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export default function ProfileImageEditor({
  onSaved
}) {

  // ───────────────────────────────────────────
  // Context
  // ───────────────────────────────────────────
  const {
    profileImage,
    siteData,
    refreshPortfolio
  } = useAdmin()

  // ───────────────────────────────────────────
  // Refs
  // ───────────────────────────────────────────
  const fileRef = useRef(null)

  // ───────────────────────────────────────────
  // Local State
  // ───────────────────────────────────────────
  const [preview, setPreview] =
    useState(null)

  const [dragging, setDragging] =
    useState(false)

  const [err, setErr] =
    useState('')

  const [loading, setLoading] =
    useState(false)

  const [saved, setSaved] =
    useState(false)

  // ───────────────────────────────────────────
  // Validate File
  // ───────────────────────────────────────────
  const validateFile = (file) => {

    if (!file) {
      return 'No file selected.'
    }

    if (
      !ALLOWED_TYPES.includes(
        file.type
      )
    ) {

      return `
        Unsupported file type.
        Use:
        ${ALLOWED_EXT}
      `
    }

    if (file.size > MAX_SIZE_B) {

      return `
        File too large.
        Max ${MAX_SIZE_MB}MB allowed.
      `
    }

    return null
  }

  // ───────────────────────────────────────────
  // Process File
  // ───────────────────────────────────────────
  const processFile =
    useCallback(async (file) => {

      const validationError =
        validateFile(file)

      if (validationError) {

        setErr(validationError)

        return
      }

      setErr('')

      setLoading(true)

      try {

        const compressed =
          await compressImage(file)

        setPreview(compressed)

      } catch (err) {

        console.error(err)

        setErr(
          'Could not process image.'
        )

      } finally {

        setLoading(false)
      }

    }, [])

  // ───────────────────────────────────────────
  // File Input
  // ───────────────────────────────────────────
  const handleFileChange = (e) => {

    const file =
      e.target.files?.[0]

    if (file) {
      processFile(file)
    }

    e.target.value = ''
  }

  // ───────────────────────────────────────────
  // Drag & Drop
  // ───────────────────────────────────────────
  const handleDrop = (e) => {

    e.preventDefault()

    setDragging(false)

    const file =
      e.dataTransfer.files?.[0]

    if (file) {
      processFile(file)
    }
  }

  // ───────────────────────────────────────────
  // Save Image
  // ───────────────────────────────────────────
  const handleSave =
    async () => {

      if (!preview) return

      try {

        setLoading(true)

        setErr('')

        // Save to backend
        await saveProfileImage(
          preview
        )

        // Refresh global state
        await refreshPortfolio()

        setPreview(null)

        setSaved(true)

        setTimeout(() => {
          setSaved(false)
        }, 2500)

        onSaved?.(
          'Profile photo updated!'
        )

      } catch (err) {

        console.error(err)

        setErr(
          err.message
          || 'Failed to save image.'
        )

      } finally {

        setLoading(false)
      }
    }

  // ───────────────────────────────────────────
  // Remove Image
  // ───────────────────────────────────────────
  const handleRemove =
    async () => {

      try {

        setLoading(true)

        setErr('')

        // Delete from backend
        await deleteProfileImage()

        // Refresh global state
        await refreshPortfolio()

        setPreview(null)

        onSaved?.(
          'Profile photo removed.'
        )

      } catch (err) {

        console.error(err)

        setErr(
          err.message
          || 'Failed to remove image.'
        )

      } finally {

        setLoading(false)
      }
    }

  // ───────────────────────────────────────────
  // Current Image
  // ───────────────────────────────────────────
  const currentImg =
    preview || profileImage

  // ───────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────
  return (

    <div
      className="
        glass-card
        rounded-2xl
        border
        border-white/5
        overflow-hidden
      "
    >

      {/* Header */}
      <div
        className="
          flex
          items-center
          justify-between
          px-6
          py-4
          border-b
          border-white/5
        "
      >

        <div>

          <h3
            className="
              font-display
              font-semibold
              text-white
              flex
              items-center
              gap-2
            "
          >

            <FaCamera
              size={14}
              className="
                text-blue-400
              "
            />

            Profile Photo

          </h3>

          <p
            className="
              text-xs
              text-slate-500
              mt-0.5
              font-mono
            "
          >

            Shown on Hero section ·
            Max {MAX_SIZE_MB}MB

          </p>

        </div>

        {profileImage && !preview && (

          <span
            className="
              flex
              items-center
              gap-1.5
              text-xs
              font-mono
              text-green-400
              px-2.5
              py-1
              rounded-lg
              bg-green-500/10
              border
              border-green-500/20
            "
          >

            <FaCheck size={9} />

            Photo set

          </span>
        )}

      </div>

      {/* Body */}
      <div className="p-6 space-y-6">

        {/* Images */}
        <div
          className="
            grid
            sm:grid-cols-2
            gap-5
          "
        >

          {/* Current */}
          <div>

            <p className="field-label mb-3">
              Current
            </p>

            <div
              className="
                w-full
                aspect-square
                max-w-[180px]
                mx-auto
                rounded-2xl
                overflow-hidden
                border
                border-white/10
                flex
                items-center
                justify-center
              "

              style={{
                background:
                  'rgba(255,255,255,0.03)'
              }}
            >

              {profileImage ? (

                <img
                  src={profileImage}
                  alt="Current profile"
                  className="
                    w-full
                    h-full
                    object-cover
                  "
                />

              ) : (

                <div
                  className="
                    flex
                    flex-col
                    items-center
                    gap-2
                    p-6
                    text-center
                  "
                >

                  <div
                    className="
                      w-14
                      h-14
                      rounded-full
                      flex
                      items-center
                      justify-center
                      text-2xl
                      font-black
                      text-white
                    "

                    style={{
                      background:
                        'linear-gradient(135deg,#6366f1,#8b5cf6)'
                    }}
                  >

                    {
                      siteData?.hero?.name
                        ?.charAt(0)
                      || '?'
                    }

                  </div>

                  <p
                    className="
                      text-xs
                      text-slate-600
                      font-mono
                    "
                  >
                    No photo uploaded
                  </p>

                </div>
              )}

            </div>

          </div>

          {/* Upload */}
          <div>

            <p className="field-label mb-3">

              {preview
                ? 'Preview'
                : 'Upload New Photo'}

            </p>

            <div

              onDragOver={(e) => {
                e.preventDefault()
                setDragging(true)
              }}

              onDragLeave={() => {
                setDragging(false)
              }}

              onDrop={handleDrop}

              onClick={() => {
                if (!preview) {
                  fileRef.current?.click()
                }
              }}

              className="
                w-full
                aspect-square
                max-w-[180px]
                mx-auto
                rounded-2xl
                overflow-hidden
                border-2
                border-dashed
                flex
                items-center
                justify-center
                cursor-pointer
              "

              style={{
                borderColor:
                  dragging
                    ? '#6366f1'
                    : 'rgba(255,255,255,0.1)'
              }}
            >

              {loading ? (

                <motion.div

                  animate={{
                    rotate: 360
                  }}

                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    ease: 'linear'
                  }}

                  className="
                    w-6
                    h-6
                    rounded-full
                    border-2
                    border-blue-400/30
                    border-t-blue-400
                  "
                />

              ) : currentImg ? (

                <img
                  src={currentImg}
                  alt="Preview"
                  className="
                    w-full
                    h-full
                    object-cover
                  "
                />

              ) : (

                <FaUpload
                  size={20}
                  className="
                    text-slate-600
                  "
                />
              )}

            </div>

          </div>

        </div>

        {/* Error */}
        <AnimatePresence>

          {err && (

            <motion.div

              initial={{
                opacity: 0,
                height: 0
              }}

              animate={{
                opacity: 1,
                height: 'auto'
              }}

              exit={{
                opacity: 0,
                height: 0
              }}

              className="
                flex
                items-center
                gap-2
                px-4
                py-3
                rounded-xl
                text-sm
                font-mono
              "

              style={{
                background:
                  'rgba(248,113,113,0.08)',

                border:
                  '1px solid rgba(248,113,113,0.2)',

                color:
                  '#f87171'
              }}
            >

              <FaExclamationTriangle
                size={12}
              />

              {err}

            </motion.div>
          )}

        </AnimatePresence>

        {/* Hidden Input */}
        <input
          ref={fileRef}
          type="file"
          accept={ALLOWED_EXT}
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Actions */}
        <div
          className="
            flex
            gap-3
            flex-wrap
          "
        >

          {/* Save */}
          {preview && (

            <motion.button

              onClick={handleSave}

              whileHover={{
                scale: 1.03
              }}

              whileTap={{
                scale: 0.97
              }}

              className="
                flex
                items-center
                gap-2
                px-4
                py-2
                rounded-xl
                text-sm
                font-semibold
                text-white
              "

              style={{
                background:
                  'linear-gradient(135deg,#6366f1,#8b5cf6)'
              }}
            >

              <FaCheck size={11} />

              Save Photo

            </motion.button>
          )}

          {/* Discard */}
          {preview && (

            <motion.button

              onClick={() => {
                setPreview(null)
                setErr('')
              }}

              className="
                px-4
                py-2
                rounded-xl
                text-sm
                font-mono
                text-slate-400
                border
                border-white/10
              "
            >

              Discard

            </motion.button>
          )}

          {/* Remove */}
          {profileImage && !preview && (

            <motion.button

              onClick={handleRemove}

              className="
                flex
                items-center
                gap-2
                px-4
                py-2
                rounded-xl
                text-sm
                font-mono
                text-red-400
                border
                border-red-500/20
                bg-red-500/5
              "
            >

              <FaTrash size={10} />

              Remove Photo

            </motion.button>
          )}

          {/* View */}
          {profileImage && (

            <motion.a

              href="/#home"

              target="_blank"

              rel="noopener noreferrer"

              className="
                flex
                items-center
                gap-2
                px-4
                py-2
                rounded-xl
                text-sm
                font-mono
                text-slate-400
                border
                border-white/10
              "
            >

              <FaEye size={10} />

              View Site

            </motion.a>
          )}

        </div>

      </div>

    </div>
  )
}